/**
 * useBatchRecovery -- Unified pending_sync poller cho cả 3 loại job (translate/audit/semantic-match)
 *
 * Pattern (giống nhau cho cả 3):
 *  1. SELECT pending_sync rows chưa synced
 *  2. Merge theo type:
 *     - translate: group by run_id, merge khi đủ batches → aiOutput
 *     - audit: per-row, set auditResult vào file đúng file_id
 *     - semantic-match: per-row, set matchStatus='done'
 *  3. DELETE rows đã apply
 *
 * Trigger:
 *  - On mount + filesCount change (recovery sau refresh)
 *  - Every 10s while user signed in (live update during chain)
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { useProjectStore } from '../store/useProjectStore';
import { supabase } from '../services/supabase/supabaseClient';

const WORKER_TIMEOUT_MS = 15 * 60_000; // 15 phut (match Cloudflare unbound wall clock)
const POLL_INTERVAL_MS = 10_000;        // 10s poll pending_sync khi có active job
const JOBS_LIVENESS_INTERVAL_MS = 15 * 60_000; // 15 phút — poll jobs để detect dead/error
const INITIAL_DELAY_MS = 1_500;         // 1.5s để store hydrate (Zustand persist + IndexedDB)

// Module-level recovery trigger — cho phép component khác (ProjectDetail) gọi doRecover ngay
// khi navigate, không đợi poll cycle. Singleton vì useBatchRecovery mount 1 lần ở App level.
let triggerRecoveryRef: (() => void) | null = null;
export function triggerRecovery() {
  if (triggerRecoveryRef) triggerRecoveryRef();
}

export function useBatchRecovery() {
  const isRecoveringRef = useRef(false);
  const [recovering, setRecovering] = useState(false);

  const doRecover = useCallback(async () => {
    if (isRecoveringRef.current) return;
    isRecoveringRef.current = true;
    setRecovering(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.log('[Recovery] No session, skip');
        return;
      }
      const userId = session.user.id;
      console.log(`[Recovery] Start for user ${userId.slice(0, 8)}`);

      // 1. Check Worker crash: jobs.status=running qua 5 phut
      const fiveMinAgo = new Date(Date.now() - WORKER_TIMEOUT_MS).toISOString();
      const { data: stuckJobs } = await supabase
        .from('jobs')
        .select('id, run_id, file_id, file_name')
        .eq('user_id', userId)
        .eq('status', 'running')
        .lt('started_at', fiveMinAgo);

      if (stuckJobs && stuckJobs.length > 0) {
        // US-19-12 Tier 1: client UPDATE on jobs revoked. Server cron `recover_stale_jobs`
        // (workers/wrangler.commercial.toml schedule */5 * * * *) handles same case
        // via service_role — refunds + marks error atomically. Client-side update
        // removed; worst case 5-min delay before stuck job recovered server-side.
        console.log(`[Recovery] ${stuckJobs.length} stuck jobs detected (running > 5min). Server cron will mark error within 5 min.`);
      }

      // 2. Fetch pending_sync — ALL types (translate + audit + semantic-match).
      // 3 loại dùng cùng cơ chế: poll → merge theo type → DELETE.
      const { data: pendingData, error: fetchErr } = await supabase
        .from('pending_sync')
        .select('id, file_id, run_id, batch_index, total_batches, translated, type, result, job_id')
        .eq('user_id', userId)
        .is('synced_at', null)
        .order('batch_index', { ascending: true });

      if (fetchErr) {
        console.error('[Recovery] Fetch pending_sync error:', fetchErr.message);
        return;
      }

      if (!pendingData || pendingData.length === 0) {
        return;
      }

      const updateFile = useProjectStore.getState().updateFile;
      const files = useProjectStore.getState().files;
      const appliedIds: string[] = [];

      const translateRows = pendingData.filter(r => r.type === 'translate');
      const auditRows = pendingData.filter(r => r.type === 'audit');
      const matchRows = pendingData.filter(r => r.type === 'semantic-match');

      console.log(`[Recovery] Found ${pendingData.length} rows (translate=${translateRows.length} audit=${auditRows.length} match=${matchRows.length})`);

      // 3a. Translate: group by run_id → merge khi đủ tất cả batches
      const byRun = new Map<string, typeof translateRows>();
      for (const row of translateRows) {
        if (!byRun.has(row.run_id)) byRun.set(row.run_id, []);
        byRun.get(row.run_id)!.push(row);
      }

      for (const [runId, rows] of byRun) {
        const totalBatches = rows[0].total_batches;
        const fileId = rows[0].file_id;

        if (rows.length < totalBatches) {
          console.log(`[Recovery] translate run ${runId.slice(0, 8)}: ${rows.length}/${totalBatches} batches — chưa đủ, SKIP`);
          continue;
        }

        const file = files.find(f => f.id === fileId);
        if (!file) {
          console.warn(`[Recovery] translate file ${fileId?.slice(0, 8)} NOT FOUND — SKIP (giữ row)`);
          continue;
        }

        const sorted = rows.sort((a, b) => a.batch_index - b.batch_index);
        const merged: string[] = [];
        for (const row of sorted) merged.push(...(row.translated || []));

        updateFile(fileId, {
          aiOutput: merged,
          translationStatus: 'done',
          currentStep: 3,
          error: undefined,
        });
        for (const row of rows) appliedIds.push(row.id);
        console.log(`[Recovery] translate "${file.fileName}": merged ${merged.length} segments`);
      }

      // 3b. Audit: per-row → set auditResult vào file đúng file_id
      for (const row of auditRows) {
        const file = files.find(f => f.id === row.file_id);
        if (!file) {
          console.warn(`[Recovery] audit file ${row.file_id?.slice(0, 8)} NOT FOUND — SKIP (giữ row)`);
          continue;
        }
        if (!row.result) {
          // Empty result → vẫn xóa để khỏi pile up
          appliedIds.push(row.id);
          continue;
        }
        const auditResult = row.result;
        updateFile(row.file_id, {
          auditResult: auditResult as any,
          auditScore: auditResult?.overallScore,
          auditRawOutput: '',
          auditStatus: 'done',
          auditUsage: auditResult?.usage,
          currentStep: 4,
          completedAt: new Date(),
          error: undefined,
        });
        appliedIds.push(row.id);
        console.log(`[Recovery] audit "${file.fileName}": auditResult applied`);
      }

      // 3c. Semantic-match: per-row → merge result vào file.semanticMatchResult
      // (UI Ghép cặp step cần mappings + alignedHumanSegments để render bảng đối chiếu)
      for (const row of matchRows) {
        const file = files.find(f => f.id === row.file_id);
        if (!file) {
          console.warn(`[Recovery] match file ${row.file_id?.slice(0, 8)} NOT FOUND — SKIP (giữ row)`);
          continue;
        }
        if (!row.result) {
          appliedIds.push(row.id);
          continue;
        }
        const matchResult = row.result;
        updateFile(row.file_id, {
          semanticMatchResult: matchResult as any,
          matchStatus: 'done',
        });
        appliedIds.push(row.id);
        console.log(`[Recovery] match "${file.fileName}": ${matchResult?.mappings?.length ?? 0} mappings applied`);
      }

      // 4. Song song: DELETE pending_sync rows + UPDATE jobs.synced_at = now() để audit.
      //    pending_sync DELETE = giải phóng storage (cleanup tự nhiên).
      //    jobs.synced_at = audit trail persistent (jobs row sống lâu hơn pending_sync).
      //    RLS policy "User update own synced_at on jobs" + column-level GRANT cho phép client UPDATE chỉ synced_at.
      if (appliedIds.length > 0) {
        // Lấy job_ids từ pending_sync rows đã apply (rows in pendingData)
        const appliedJobIds = pendingData
          .filter(r => appliedIds.includes(r.id) && r.job_id)
          .map(r => r.job_id);

        // Run song song để giảm latency
        const [deleteResult, updateResult] = await Promise.allSettled([
          supabase.from('pending_sync').delete().in('id', appliedIds),
          appliedJobIds.length > 0
            ? supabase.from('jobs').update({ synced_at: new Date().toISOString() }).in('id', appliedJobIds)
            : Promise.resolve({ error: null }),
        ]);

        if (deleteResult.status === 'rejected' || (deleteResult.status === 'fulfilled' && (deleteResult.value as any).error)) {
          console.error('[Recovery] Delete pending_sync error:', deleteResult.status === 'fulfilled' ? (deleteResult.value as any).error?.message : deleteResult.reason);
        } else {
          console.log(`[Recovery] Cleaned ${appliedIds.length} pending_sync rows`);
        }

        if (updateResult.status === 'rejected' || (updateResult.status === 'fulfilled' && (updateResult.value as any).error)) {
          console.warn('[Recovery] Update jobs.synced_at error (non-fatal):', updateResult.status === 'fulfilled' ? (updateResult.value as any).error?.message : updateResult.reason);
        } else if (appliedJobIds.length > 0) {
          console.log(`[Recovery] Marked ${appliedJobIds.length} jobs synced`);
        }
      }

    } catch (err) {
      console.error('[Recovery] Error:', err);
    } finally {
      isRecoveringRef.current = false;
      setRecovering(false);
    }
  }, []);

  // Watch files.length — trigger recover khi:
  // - Mount (5s delay cho store hydrate)
  // - files.length đổi từ 0 → N (sau folder permission re-grant + syncFromDisk hoàn thành)
  // Tránh race: pending_sync có data nhưng files store rỗng → SKIP → không retry → translation stuck
  const filesCount = useProjectStore(s => s.files.length);
  // Gate cho polling: chỉ poll khi có job thực sự đang chạy (không phải mọi file)
  // Tiết kiệm Disk IO trên Supabase Free Nano — user idle browse files đã done → 0 polling.
  const hasActiveJob = useProjectStore(s => s.files.some(f =>
    f.translationStatus === 'running' ||
    f.matchStatus === 'running' ||
    f.auditStatus === 'running'
  ));
  const lastRecoveredCountRef = useRef(-1);

  useEffect(() => {
    // Skip nếu đã recover lần này hoặc count chưa đổi
    if (filesCount === lastRecoveredCountRef.current) return;

    // Delay ngắn (1.5s) cho store hydrate lần đầu, sau đó re-trigger ngay khi files load
    const isFirstRun = lastRecoveredCountRef.current === -1;
    const delay = isFirstRun ? INITIAL_DELAY_MS : 200;

    const timer = setTimeout(() => {
      lastRecoveredCountRef.current = filesCount;
      doRecover();
    }, delay);
    return () => clearTimeout(timer);
  }, [doRecover, filesCount]);

  // Polling for in-session updates — CHỈ chạy khi có active job (translate/match/audit running).
  // Server xong job → INSERT pending_sync → poll picks it up → merge + UPDATE synced_at.
  // Khi tất cả file done/pending → useEffect cleanup → clearInterval → ngừng polling.
  useEffect(() => {
    if (!hasActiveJob) return;
    const interval = setInterval(() => {
      // Pause khi tab background — Chrome throttle setInterval anyway, nhưng explicit guard cleaner
      if (document.visibilityState === 'visible') doRecover();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [doRecover, hasActiveJob]);

  // Jobs liveness polling 15 phút — detect dead/errored jobs server-side.
  // Khác với pending_sync polling (10s): ít hơn nhiều, chỉ check jobs.status để biết
  // server đã mark error/done chưa. 4 case xử lý:
  //   - status='error' → flip file error + show server message
  //   - status='done' + pending_sync rows → trigger doRecover merge bình thường
  //   - status='done' + no rows + aiOutput có data → flip done (đã merge ở session trước)
  //   - status='done' + no rows + aiOutput rỗng → flip error "Kết quả không có trên máy này"
  useEffect(() => {
    if (!hasActiveJob) return;

    const checkJobsLiveness = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;
        const userId = session.user.id;

        const files = useProjectStore.getState().files;
        const updateFile = useProjectStore.getState().updateFile;
        const runningFiles = files.filter(f =>
          f.translationStatus === 'running' ||
          f.matchStatus === 'running' ||
          f.auditStatus === 'running'
        );
        if (runningFiles.length === 0) return;

        const fileIds = runningFiles.map(f => f.id);
        const { data: terminalJobs, error } = await supabase
          .from('jobs')
          .select('file_id, status, error, type')
          .eq('user_id', userId)
          .in('status', ['done', 'error'])
          .in('file_id', fileIds);

        if (error) {
          console.warn('[Liveness] Jobs query failed:', error.message);
          return;
        }
        if (!terminalJobs || terminalJobs.length === 0) return;

        // Get latest pending_sync rows still un-synced cho các file_id liên quan
        const { data: unsyncedRows } = await supabase
          .from('pending_sync')
          .select('file_id, type')
          .eq('user_id', userId)
          .is('synced_at', null)
          .in('file_id', fileIds);
        const hasUnsyncedKey = new Set(
          (unsyncedRows || []).map(r => `${r.file_id}::${r.type}`)
        );

        for (const job of terminalJobs) {
          const file = files.find(f => f.id === job.file_id);
          if (!file) continue;

          const statusField = job.type === 'audit' ? 'auditStatus'
                            : job.type === 'semantic-match' ? 'matchStatus'
                            : 'translationStatus';

          // Skip nếu local đã không còn 'running' cho field này (đã được sync rồi)
          if ((file as any)[statusField] !== 'running') continue;

          if (job.status === 'error') {
            updateFile(job.file_id, {
              [statusField]: 'error',
              error: job.error || 'Job thất bại trên server. Bấm để thử lại.',
            } as any);
            console.log(`[Liveness] File ${job.file_id.slice(0,8)} ${job.type} → error (${job.error})`);
            continue;
          }

          // status === 'done'
          const stillSyncable = hasUnsyncedKey.has(`${job.file_id}::${job.type}`);
          if (stillSyncable) {
            // pending_sync rows chưa merge — trigger doRecover lấy về
            doRecover();
            continue;
          }

          // status='done' + không còn pending_sync row → kiểm tra local có data chưa
          const hasResult = job.type === 'translate'
            ? !!(file.aiOutput && file.aiOutput.length > 0)
            : job.type === 'audit'
              ? !!file.auditResult
              : !!file.semanticMatchResult;

          if (hasResult) {
            // Đã merge ở session trước, chỉ cần flip status
            updateFile(job.file_id, { [statusField]: 'done' } as any);
            console.log(`[Liveness] File ${job.file_id.slice(0,8)} ${job.type} → done (already merged)`);
          } else {
            // Result mất luôn — báo error cho user retry
            updateFile(job.file_id, {
              [statusField]: 'error',
              error: 'Đã dịch xong nhưng kết quả không có trên máy này. Vui lòng dịch lại.',
            } as any);
            console.warn(`[Liveness] File ${job.file_id.slice(0,8)} ${job.type} → result lost, marked error`);
          }
        }
      } catch (e: any) {
        console.warn('[Liveness] Error:', e?.message ?? e);
      }
    };

    // Chạy lần đầu sau 15p (không chạy ngay vì doRecover initial đã handle)
    const interval = setInterval(checkJobsLiveness, JOBS_LIVENESS_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [doRecover, hasActiveJob]);

  // Expose doRecover via module-level singleton — ProjectDetail/Tab navigation gọi để
  // recover ngay (không đợi poll cycle).
  useEffect(() => {
    triggerRecoveryRef = () => doRecover();
    return () => { triggerRecoveryRef = null; };
  }, [doRecover]);

  // Visibility change: user switch tab quay lại → recover ngay (catch up Chrome background throttle)
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible' && hasActiveJob) doRecover();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [doRecover, hasActiveJob]);

  return { recovering };
}

